/**
 * Media library: the picker that reuses pictures the merchant already has.
 * Conventions: see `common.ts`.
 */
const media: Record<string, string> = {
  'library.title': 'Select from Media Library',
  'library.search_label': 'Search your images',
  'library.search_placeholder': 'Search by product, category or post…',
  'library.all_images': 'All images',
  'library.load_failed': 'We could not load your images.',
  'library.no_matches_title': 'Nothing matches that',
  'library.no_matches_description': 'Try a different word, or clear the filter.',
  'library.empty_title': 'No images yet',
  'library.empty_description': 'Upload an image here and it will be waiting in your library next time.',
  'library.add_image': 'Add image',
  'library.add_images': 'Add {count} images',
  'library.total_images': '{count} images',
  // The Stock tab: the platform's own pictures for the store's industry.
  'library.stock_note': 'Free pictures chosen for your kind of business. Use them anywhere in your store.',
  'library.stock_empty_title': 'No stock pictures yet',
  'library.stock_empty_description': 'Pictures for your kind of business will appear here as we add them.',

  // Where a picture came from - the filter chips.
  'kind.ai_generated': 'Made with AI',
  'kind.product': 'Product photos',
  'kind.category': 'Category images',
  'kind.blog': 'Blog covers',
  'kind.store': 'Store branding',
  'kind.theme': 'Theme banners',
  'kind.stock': 'Stock',

  // Camera capture: taking a photo straight into an image slot, or picking one
  // from the device, then cropping and checking it before it is used. Keys
  // under `types.` are the image types in `lib/imageSpecs.ts`.
  'capture.types.PRODUCT': 'Product image',
  'capture.types.CATEGORY': 'Category image',
  'capture.types.LOGO': 'Logo',
  'capture.types.FAVICON': 'Favicon',
  'capture.types.SIGNATURE': 'Signature',
  'capture.types.HERO_BANNER': 'Hero banner',
  'capture.types.PROMOTIONAL_BANNER': 'Promotional banner',
  'capture.types.COLLECTION_BANNER': 'Collection banner',
  'capture.types.SLIDE': 'Slide image',
  'capture.types.SECTION_IMAGE': 'Section image',
  'capture.types.GALLERY': 'Gallery image',
  'capture.types.SERVICE': 'Service image',
  'capture.types.BLOG_COVER': 'Blog cover',
  'capture.types.SOCIAL': 'Social media post',
  'capture.types.AI_SOURCE': 'Photo for AI Image Studio',

  'capture.title': '{type} — {ratio}',
  'capture.recommended': 'Recommended {width} × {height}',
  'capture.aspect_label': 'Shape',
  'capture.aspect_free': 'Free',
  'capture.viewfinder': 'Camera viewfinder',

  // What to do inside the frame, by how the photo will be shown.
  'capture.instructions.card': 'Position your product inside the frame.',
  'capture.instructions.banner': 'Compose your photo inside the wide frame.',
  'capture.instructions.logo': 'Place your logo inside the frame, on a plain background.',
  'capture.instructions.cover': 'Keep the main subject inside the frame.',
  'capture.instructions.plain': 'Frame your photo inside the guide.',
  'capture.instructions.crop': 'Drag the frame or its corners to choose what is kept.',

  'capture.buttons.take_photo': 'Take photo',
  'capture.buttons.upload_from_device': 'Upload from device',
  'capture.buttons.use_phone_camera': 'Use phone camera app',
  'capture.buttons.retake': 'Retake',
  'capture.buttons.adjust_crop': 'Adjust crop',
  'capture.buttons.use_photo': 'Use photo',
  'capture.buttons.switch_camera': 'Switch camera',
  'capture.buttons.capture': 'Take the picture',
  'capture.buttons.edit_in_ai_studio': 'Edit in AI Image Studio',
  'capture.buttons.remove_background': 'Remove background with AI',
  'capture.buttons.try_again': 'Try again',
  'capture.buttons.close': 'Close camera',
  'capture.buttons.finish': 'Done',
  'capture.buttons.cancel_crop': 'Cancel',

  'capture.status.starting': 'Starting the camera…',
  'capture.status.requesting': 'Allow camera access in your browser to continue.',
  'capture.status.processing': 'Preparing your photo…',
  'capture.status.saving': 'Saving your photo…',
  'capture.status.added': 'Photo added. Take another, or tap Done.',

  'capture.errors.permission_denied':
    'Camera access is blocked. Allow it in your browser settings, or upload a photo instead.',
  'capture.errors.camera_unavailable':
    'We could not find a camera on this device. You can upload a photo instead.',
  'capture.errors.unsupported_browser':
    'This browser cannot open the camera here. Use your phone camera app or upload a photo instead.',
  'capture.errors.camera_busy':
    'Another app is using the camera. Close it and try again, or upload a photo instead.',
  'capture.errors.cancelled': 'Camera access was not allowed. Try again, or upload a photo instead.',
  'capture.errors.upload_failed': 'We could not save your photo. Please try again.',
  'capture.errors.network': 'We could not reach the server. Check your connection and try again.',
  'capture.errors.too_large': 'This image is too large to upload. Please use one under {size}.',
  'capture.errors.low_resolution':
    'This photo is only {width} × {height} pixels, so it may look blurry here.',
  'capture.errors.processing_failed': 'We could not prepare that photo. Please try another one.',

  'capture.preview.title': 'Check your photo',
  'capture.preview.as_saved': 'As it will be saved',
  'capture.preview.dimensions': '{width} × {height} px',
  'capture.preview.in_card': 'Preview on a card',
  'capture.preview.in_banner': 'Preview in banner',
  'capture.preview.in_header': 'Preview in your store header',
  'capture.preview.in_cover': 'Preview as a blog cover',
  'capture.preview.light_header': 'On a light header',
  'capture.preview.dark_header': 'On a dark header',
  'capture.preview.image_alt': 'Your photo, as it will be saved',

  'capture.cropper.title': 'Adjust crop',
  'capture.cropper.area': 'Crop area',
  'capture.cropper.corner': 'Resize crop from this corner',
};

export default media;
